import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeImage from '@/components/base/SafeImage';
import {
  fetchPublicTestimonials,
  fetchTestimonialsSection,
  DEFAULT_TESTIMONIAL_SECTION,
  type Testimonial,
  type TestimonialSection,
} from '@/services/testimonials';

// Carrusel público "Testimonios de la Manada". Se muestra en el home de la
// tienda justo después del catálogo. Combina fotos/videos (normalmente
// capturas de WhatsApp) con el mensaje del cliente y nuestra respuesta de
// bienvenida. Incluye un filtro por producto para ver las reseñas de cada
// referencia. Todo el contenido es editable desde /admin/testimonios.
export default function TestimonialsCarousel() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [section, setSection] = useState<TestimonialSection>(DEFAULT_TESTIMONIAL_SECTION);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(1);
  const [paused, setPaused] = useState(false);
  const [productFilter, setProductFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchPublicTestimonials(), fetchTestimonialsSection()]).then(([rows, cfg]) => {
      if (cancelled) return;
      setItems(rows);
      setSection(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cuántas tarjetas se ven a la vez según el ancho de pantalla.
  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      if (w >= 1024) setPerView(3);
      else if (w >= 640) setPerView(2);
      else setPerView(1);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Productos que tienen al menos un testimonio (para el filtro del carrusel).
  const productOptions = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((t) => {
      if (!t.productName) return;
      const key = t.productSlug || t.productName;
      if (!map.has(key)) map.set(key, t.productName);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [items]);

  const filteredItems = useMemo(() => {
    if (!productFilter) return items;
    return items.filter((t) => (t.productSlug || t.productName) === productFilter);
  }, [items, productFilter]);

  const maxIndex = Math.max(0, filteredItems.length - perView);

  // Al cambiar de filtro volvemos al inicio del grupo.
  useEffect(() => {
    setIndex(0);
  }, [productFilter]);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  // Avance automático, se pausa al pasar el mouse.
  useEffect(() => {
    if (paused || maxIndex === 0) return;
    const timer = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, maxIndex]);

  if (!section.enabled || items.length === 0) return null;

  const go = (dir: -1 | 1) => {
    setIndex((i) => {
      const next = i + dir;
      if (next < 0) return maxIndex;
      if (next > maxIndex) return 0;
      return next;
    });
  };

  return (
    <section id="testimonios" className="py-16 md:py-20 bg-secondary-50 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">
            Lo que dice la manada
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-3 mb-3">
            {section.title}
          </h2>
          {section.subtitle && (
            <p className="text-foreground-600 max-w-lg mx-auto text-sm md:text-base">
              {section.subtitle}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 mt-5">
            <span className="h-px w-16 bg-background-200"></span>
            <span className="w-8 h-8 flex items-center justify-center text-primary-500">
              <i className="ri-heart-3-line text-2xl"></i>
            </span>
            <span className="h-px w-16 bg-background-200"></span>
          </div>
        </div>

        {/* Filtro por producto: muestra las reseñas de cada referencia */}
        {productOptions.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            <button
              type="button"
              onClick={() => setProductFilter('')}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer border ${
                productFilter === ''
                  ? 'bg-accent-500 text-white border-accent-500'
                  : 'bg-white text-foreground-600 border-background-200 hover:border-accent-300 hover:text-accent-700'
              }`}
            >
              Todos ({items.length})
            </button>
            {productOptions.map((opt) => {
              const count = items.filter((t) => (t.productSlug || t.productName) === opt.value).length;
              const active = productFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setProductFilter(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer border ${
                    active
                      ? 'bg-accent-500 text-white border-accent-500'
                      : 'bg-white text-foreground-600 border-background-200 hover:border-accent-300 hover:text-accent-700'
                  }`}
                >
                  <i className={`ri-shopping-bag-3-line ${active ? 'text-white' : 'text-secondary-500'}`}></i>
                  {opt.label} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {maxIndex > 0 && (
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Testimonio anterior"
              className="hidden sm:flex absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-background-200 text-foreground-600 hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
          )}

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
            >
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="flex-shrink-0 px-2.5"
                  style={{ width: `${100 / perView}%` }}
                >
                  <article className="h-full bg-white rounded-2xl border border-background-200 overflow-hidden flex flex-col">
                    <div className="relative aspect-[4/5] bg-background-100 overflow-hidden">
                      {item.mediaType === 'video' ? (
                        <video
                          src={item.mediaUrl}
                          controls
                          playsInline
                          className="w-full h-full object-cover bg-black"
                        />
                      ) : (
                        <SafeImage
                          src={item.mediaUrl}
                          alt="Testimonio de cliente"
                          title="Testimonio de la manada"
                          className="w-full h-full object-cover object-top"
                        />
                      )}
                    </div>
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      {item.caption && (
                        <p className="text-sm text-foreground-700 leading-relaxed">
                          &ldquo;{item.caption}&rdquo;
                        </p>
                      )}
                      {item.productName && (
                        item.productSlug ? (
                          <Link
                            to={`/producto/${item.productSlug}`}
                            title={`Ver ${item.productName}`}
                            className="group inline-flex items-center gap-1.5 self-start mt-3 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                          >
                            <i className="ri-shopping-bag-3-line text-secondary-600"></i>
                            Compró: {item.productName}
                            <i className="ri-arrow-right-up-line text-secondary-500 group-hover:text-secondary-700"></i>
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 self-start mt-3 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium">
                            <i className="ri-shopping-bag-3-line text-secondary-600"></i>
                            Compró: {item.productName}
                          </span>
                        )
                      )}
                      {item.clientName && (
                        <p className="text-xs font-semibold text-foreground-900 mt-3">
                          {item.clientName}
                        </p>
                      )}
                      {item.reply && (
                        <div className="mt-3 pt-3 border-t border-background-100 flex items-start gap-2">
                          <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-heart-pulse-line text-accent-600 text-xs"></i>
                          </span>
                          <p className="text-xs text-accent-700 leading-relaxed">
                            {item.reply}
                          </p>
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {maxIndex > 0 && (
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Siguiente testimonio"
              className="hidden sm:flex absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border border-background-200 text-foreground-600 hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          )}
        </div>

        {maxIndex > 0 && (
          <div className="flex items-center justify-center gap-2 mt-7">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Ir al grupo ${i + 1}`}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-300 hover:bg-background-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}